import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_1b05ad9b-00ea-4f44-a8ab-b9119f3f327f/artifacts/eb517133_Design%20ohne%20Titel%20%2811%29.png";

const Impressum = () => {
  const { dir, lang } = useLanguage();

  const backLabel = {
    ar: "العودة إلى الصفحة الرئيسية",
    de: "Zurück zur Startseite",
    en: "Back to home",
    fa: "بازگشت به صفحه اصلی",
  }[lang] || "Zurück zur Startseite";

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 100% 0%, rgba(31,107,122,0.10) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 0% 100%, rgba(232,163,101,0.08) 0%, transparent 60%)",
        }}
      />

      <header className="border-b border-[rgba(31,107,122,0.12)] bg-white/70 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="impressum-logo">
            <img
              src={LOGO_URL}
              alt="Kompass Heidelberg"
              className="object-contain"
              style={{
                mixBlendMode: "multiply",
                height: "clamp(64px, 8vw, 96px)",
                width: "auto",
              }}
            />
          </Link>
          <Link
            to="/"
            data-testid="impressum-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--kompass-primary)] hover:text-[color:var(--kompass-primary-dark)] transition-colors"
          >
            <ArrowLeft className={`h-4 w-4 ${dir === "rtl" ? "flip-x" : ""}`} strokeWidth={1.7} />
            {backLabel}
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16" dir="ltr">
        <span className="eyebrow">Rechtliches</span>
        <h1 className="display display-1 mt-4 text-[color:var(--kompass-ink)]">Impressum</h1>
        <p className="mt-3 text-sm text-[color:var(--kompass-muted)]">
          Angaben gemäß § 5 DDG
        </p>

        <div className="mt-10 max-w-none text-[color:var(--kompass-ink-soft)] leading-relaxed space-y-7 text-[15px]">
          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              Anbieter / Vertreten durch:
            </h2>
            <p>
              Ibrahim Zaaboub
              <br />
              Geschäftsbezeichnung: Kompass — Service for Migrants
              <br />
              Eppelheimer Str. 21
              <br />
              69115 Heidelberg
              <br />
              Deutschland
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              Kontakt
            </h2>
            <p>
              Telefon:{" "}
              <a
                href="tel:+491781647873"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                +49 178 164 78 73
              </a>
              <br />
              E-Mail:{" "}
              <a
                href="mailto:info@kompass-heidelberg.de"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                info@kompass-heidelberg.de
              </a>
              <br />
              Web:{" "}
              <a
                href="https://www.kompass-heidelberg.de"
                target="_blank"
                rel="noreferrer"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                www.kompass-heidelberg.de
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV
            </h2>
            <p>
              Ibrahim Zaaboub
              <br />
              Eppelheimer Str. 21, 69115 Heidelberg
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              Steuernummer
            </h2>
            <p>
              Steuernummer: <span>32100/52774</span>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              EU-Streitschlichtung
            </h2>
            <p>
              Die Europäische Kommission stellt eine Plattform zur
              Online-Streitbeilegung (OS) bereit:{" "}
              <a
                href="https://ec.europa.eu/consumers/odr/"
                target="_blank"
                rel="noreferrer"
                className="text-[color:var(--kompass-primary)] hover:underline"
              >
                https://ec.europa.eu/consumers/odr/
              </a>
              .
            </p>
            <p className="mt-3">
              Unsere E-Mail-Adresse finden Sie oben im Impressum.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[color:var(--kompass-ink)] mb-2">
              Verbraucherstreitbeilegung / Universalschlichtungsstelle
            </h2>
            <p>
              Wir sind nicht bereit oder verpflichtet, an
              Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
              teilzunehmen.
            </p>
          </section>

          <section className="pt-4 border-t border-[rgba(31,107,122,0.1)]">
            <p className="text-xs text-[color:var(--kompass-muted)]">
              Stand: {new Date().toLocaleDateString("de-DE", {
                year: "numeric",
                month: "long",
              })}
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Impressum;
